import React, { useEffect, useState } from 'react'
import {
  ArrowRightLeft,
  CheckCircle,
  Clock,
  Download,
  Eye,
  Package,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import z from 'zod'

import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog'
import { DiscreteLoader } from '@/app/components/ui/discrete-loader'
import { FormError } from '@/app/components/ui/FormError'
import { Input } from '@/app/components/ui/input'
import { Textarea } from '@/app/components/ui/textarea'
import { ConfirmationModal, ModalPreset } from '@/app/components/ui/ConfirmationModal'

import { SendReviewModal } from './SendReviewModal'
import { ReceiveConfirmModal } from './ReceiveConfirmModal'
import { ExportModal, type ExportFormat } from '@/app/components/ui/ExportModal'
import { layoutService } from '@/app/services/layout.service'
import {
  InventoryTransfer,
  type TransferExportFormat,
  TransferListItem,
  TransferProductItem,
  TransferRequest,
  transferService,
} from '@/app/services/transfer.service'
import { useAuthStore } from '@/app/store/auth/auth.store'

const formSchema = z.object({
  fromBranch: z.string().min(1, 'La sucursal origen es obligatoria'),
  toBranch: z.string().min(1, 'La sucursal destino es obligatoria'),
  notes: z.string().optional(),
  products: z.array(
    z.object({
      productId: z.string().min(1),
      productName: z.string().optional(),
      barcode: z.string().optional(),
      stock: z.number().optional(),
      stocks: z.array(z.any()).optional(),
      quantity: z.string().min(1, 'Requerido').refine((val) => parseInt(val) > 0, 'Debe ser > 0'),
    })
  ).min(1, 'Debes agregar al menos un producto'),
}).refine((data) => data.fromBranch !== data.toBranch, {
  message: 'La sucursal origen y destino deben ser diferentes.',
  path: ['toBranch'],
})

type FormValidate = z.infer<typeof formSchema>
type TransferAction = 'request' | 'send' | 'receive'
type ApiListResponse<T> = T[] | { data?: T[] | { data?: T[] } }
type TransferExportSelection = {
  transferRequestIds?: number[]
  inventoryTransferIds?: number[]
} | null

interface CatalogProductOption {
  id: number
  name?: string
  description?: string
  barcode?: string
  stocks?: any[]
}

interface TransferModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  refreshKey?: number
  onSendReview?: (transfer: TransferListItem) => void
  onReceiveConfirm?: (transfer: TransferListItem) => void
}

function unwrapList<T>(response: ApiListResponse<T>): T[] {
  const firstLevel = Array.isArray(response) ? response : response.data
  if (Array.isArray(firstLevel)) return firstLevel
  if (firstLevel && Array.isArray(firstLevel.data)) return firstLevel.data
  return []
}

function mapRequest(t: TransferRequest): TransferListItem {
  return {
    id: t.id.toString(),
    date: t.created_at?.split('T')[0] || 'Fecha',
    fromBranch: t.origin_sucursal?.name || `Sucursal ${t.origin_sucursal_id}`,
    toBranch: t.destination_sucursal?.name || `Sucursal ${t.destination_sucursal_id}`,
    products: (t.items || []).map((i: TransferProductItem) => ({
      name: i.product?.name || `Producto #${i.product_id}`,
      quantity: Number(i.requested_quantity || i.quantity || 0),
    })),
    status: 'pending',
    _originalData: t,
    _type: 'request',
  }
}

function mapTransfer(t: InventoryTransfer): TransferListItem {
  return {
    id: t.id.toString(),
    date: t.created_at?.split('T')[0] || 'Fecha',
    fromBranch: t.origin_sucursal?.name || `Sucursal ${t.origin_sucursal_id}`,
    toBranch: t.destination_sucursal?.name || `Sucursal ${t.destination_sucursal_id}`,
    products: (t.items || [])
      .filter((i: TransferProductItem) => Number(i.approved_quantity || 0) > 0)
      .map((i: TransferProductItem) => ({
        name: i.product?.name || `Producto #${i.product_id}`,
        quantity: Number(i.approved_quantity || 0),
      })),
    status: 'pending',
    _originalData: t,
    _type: 'transfer',
  }
}

function getApiMessage(error: unknown, fallback: string) {
  const responseError = error as { response?: { data?: { message?: string } } }
  return responseError.response?.data?.message || fallback
}

function filenameFromDisposition(disposition: string | undefined, fallback: string) {
  if (!disposition) return fallback

  const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1].replace(/"/g, ''))
  }

  const match = disposition.match(/filename="?([^";]+)"?/i)
  return match?.[1] || fallback
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export function TransferModal({
  open,
  onOpenChange,
  refreshKey = 0,
  onSendReview,
  onReceiveConfirm,
}: TransferModalProps) {
  const { activeBranchId, canAction } = useAuthStore()
  const canRequestTransfer = canAction('inventory.catalog-transfer.request')
  const canSendTransfer = canAction('inventory.catalog-transfer.send')
  const canReceiveTransfer = canAction('inventory.catalog-transfer.receive')
  const canExportTransfer = canAction('inventory.catalog-transfer.export')
  const availableActions = [
    canSendTransfer ? 'send' : null,
    canRequestTransfer ? 'request' : null,
    canReceiveTransfer ? 'receive' : null,
  ].filter(Boolean) as TransferAction[]
  const [transferAction, setTransferAction] = useState<TransferAction>('send')
  const [myPendingRequests, setMyPendingRequests] = useState<TransferListItem[]>([])
  const [pendingSends, setPendingSends] = useState<TransferListItem[]>([])
  const [pendingReceives, setPendingReceives] = useState<TransferListItem[]>([])
  const [sentPendingTransfers, setSentPendingTransfers] = useState<TransferListItem[]>([])
  const [isLoadingPending, setIsLoadingPending] = useState(false)
  const [refreshCount, setRefreshCount] = useState(0)

  // Export Modal State
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [exportSelection, setExportSelection] = useState<TransferExportSelection>(null)

  // Internal Navigation State
  const [showInternalSendReview, setShowInternalSendReview] = useState(false)
  const [internalTransferToReview, setInternalTransferToReview] = useState<any>(null)

  const [showInternalReceiveConfirm, setShowInternalReceiveConfirm] = useState(false)
  const [internalTransferToReceive, setInternalTransferToReceive] = useState<any>(null)

  const [activeBranchName, setActiveBranchName] = useState<string>('Mi Sucursal')

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    preset: ModalPreset;
    itemName: string;
    onConfirm: () => void;
  }>({
    open: false,
    preset: 'delete',
    itemName: '',
    onConfirm: () => { },
  })

  useEffect(() => {
    if (activeBranchId) {
      layoutService.getMySucursales().then(branches => {
        const branch = branches.find(b => b.id === activeBranchId)
        if (branch) setActiveBranchName(branch.name)
      })
    }
  }, [activeBranchId])

  // ─── Formulario ──────────────────────────────────────────
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormValidate>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      fromBranch: '',
      toBranch: '',
      notes: '',
      products: [],
    },
  })

  const { fields: productFields, append, remove, update, replace } = useFieldArray({
    control,
    name: 'products',
  })

  const watchFromBranch = watch('fromBranch')
  const watchToBranch = watch('toBranch')
  const hasCurrentActionPermission = availableActions.includes(transferAction)

  useEffect(() => {
    if (!open) return

    if (!hasCurrentActionPermission && availableActions.length > 0) {
      setTransferAction(availableActions[0])
    }
  }, [availableActions, hasCurrentActionPermission, open])

  const clearPendingLists = () => {
    setMyPendingRequests([])
    setPendingSends([])
    setPendingReceives([])
    setSentPendingTransfers([])
  }

  const handleActionChange = (action: TransferAction) => {
    if (!availableActions.includes(action)) {
      toast.error('No tienes permiso para esta operacion de transferencia')
      return
    }

    setTransferAction(action)
    if (activeBranchId) {
      if (action === 'send') {
        reset({
          fromBranch: activeBranchId.toString(),
          toBranch: '',
          notes: '',
          products: [],
        })
      } else if (action === 'request') {
        reset({
          fromBranch: '',
          toBranch: activeBranchId.toString(),
          notes: '',
          products: [],
        })
      }
    } else {
      reset()
    }
  }

  useEffect(() => {
    if (!open || !activeBranchId) return

    if (transferAction === 'send') {
      reset({
        fromBranch: activeBranchId.toString(),
        toBranch: '',
        notes: '',
        products: [],
      })
      return
    }

    if (transferAction === 'request') {
      reset({
        fromBranch: '',
        toBranch: activeBranchId.toString(),
        notes: '',
        products: [],
      })
    }
  }, [transferAction, activeBranchId, open, reset])

  useEffect(() => {
    if (!open || !activeBranchId || !hasCurrentActionPermission) return
    clearPendingLists()

    let shouldIgnoreResponse = false
    const requestedBranchId = activeBranchId
    const requestedAction = transferAction

    const fetchPending = async () => {
      setIsLoadingPending(true)
      try {
        if (requestedAction === 'request') {
          const res = await transferService.getMyPendingRequests(requestedBranchId)
          if (shouldIgnoreResponse) return

          setMyPendingRequests(unwrapList<TransferRequest>(res).map(mapRequest))
        }

        if (requestedAction === 'send') {
          const [requestsRes, transfersRes] = await Promise.all([
            transferService.getRequestsToShip(requestedBranchId),
            transferService.getSentTransfers(requestedBranchId),
          ])
          if (shouldIgnoreResponse) return

          setPendingSends(
            unwrapList<TransferRequest>(requestsRes)
              .filter((request) => Number(request.origin_sucursal_id) === Number(requestedBranchId))
              .map(mapRequest)
          )
          setSentPendingTransfers(
            unwrapList<InventoryTransfer>(transfersRes)
              .filter((transfer) => (
                Number(transfer.origin_sucursal_id) === Number(requestedBranchId) &&
                transfer.status === 'PENDING_RECEPTION' &&
                (transfer.items || []).some((item) => Number(item.approved_quantity || 0) > 0)
              ))
              .map(mapTransfer)
          )
        }

        if (requestedAction === 'receive') {
          const res = await transferService.getPendingReceptions(requestedBranchId)
          if (shouldIgnoreResponse) return

          setPendingReceives(
            unwrapList<InventoryTransfer>(res)
              .filter((transfer) => (
                Number(transfer.destination_sucursal_id) === Number(requestedBranchId) &&
                (transfer.items || []).some((item) => Number(item.approved_quantity || 0) > 0)
              ))
              .map(mapTransfer)
          )
        }



      } catch (error) {
        if (!shouldIgnoreResponse) {
          toast.error(getApiMessage(error, 'Error al cargar transferencias pendientes'))
        }
      } finally {
        if (!shouldIgnoreResponse) {
          setIsLoadingPending(false)
        }
      }
    }

    fetchPending()


    return () => {
      shouldIgnoreResponse = true
    }
  }, [open, transferAction, activeBranchId, refreshCount, refreshKey, hasCurrentActionPermission])

  const refreshPending = () => setRefreshCount((prev) => prev + 1)

  const addProductFromSearch = (product: CatalogProductOption | null) => {
    if (!product) return

    const productIdStr = product.id.toString()
    const existingIndex = productFields.findIndex((p) => p.productId === productIdStr)

    const fromBranchId = watchFromBranch;
    let branchStock = 0;
    if (product.stocks && Array.isArray(product.stocks)) {
      const stockObj = product.stocks.find((s: any) => s.sucursal_id?.toString() === fromBranchId?.toString());
      if (stockObj) {
        branchStock = stockObj.actual_stock ?? stockObj.stock ?? 0;
      }
    }

    if (existingIndex >= 0) {
      const currentQty = parseInt(productFields[existingIndex].quantity) || 0
      update(existingIndex, {
        ...productFields[existingIndex],
        quantity: (currentQty + 1).toString(),
        stock: branchStock,
        stocks: product.stocks || [],
      })
      return
    }

    append({
      productId: productIdStr,
      productName: product.name || product.description || `ID: ${product.id}`,
      barcode: (product as any).barcode || '',
      stock: branchStock,
      stocks: product.stocks || [],
      quantity: '1',
    })
  }

  useEffect(() => {
    const currentProducts = getValues('products') || []
    let modified = false
    const updatedProducts = currentProducts.map(p => {
      let branchStock = 0;
      if (p.stocks && Array.isArray(p.stocks)) {
        const stockObj = p.stocks.find((s: any) => s.sucursal_id?.toString() === watchFromBranch?.toString())
        if (stockObj) {
          branchStock = stockObj.actual_stock ?? stockObj.stock ?? 0
        }
      }
      if (p.stock !== branchStock) {
        modified = true
        return { ...p, stock: branchStock }
      }
      return p
    })

    if (modified) {
      replace(updatedProducts)
      trigger('products')
    }
  }, [watchFromBranch, getValues, replace, trigger])

  const onSubmit = async (formData: FormValidate) => {
    if (transferAction === 'request' && !canRequestTransfer) {
      toast.error('No tienes permiso para solicitar transferencias')
      return
    }

    if (transferAction === 'send' && !canSendTransfer) {
      toast.error('No tienes permiso para enviar transferencias')
      return
    }

    try {
      if (transferAction === 'request') {
        await transferService.createTransferRequest({
          origin_sucursal_id: parseInt(formData.fromBranch),
          destination_sucursal_id: parseInt(formData.toBranch),
          notes: formData.notes,
          items: formData.products.map((p) => ({
            product_id: parseInt(p.productId),
            requested_quantity: parseInt(p.quantity),
          })),
        })
        toast.success('Solicitud de transferencia creada exitosamente')
      }

      if (transferAction === 'send') {
        await transferService.createDirectTransfer({
          origin_sucursal_id: parseInt(formData.fromBranch),
          destination_sucursal_id: parseInt(formData.toBranch),
          notes: formData.notes,
          items: formData.products.map((p) => ({
            product_id: parseInt(p.productId),
            quantity: parseInt(p.quantity),
          })),
        })
        toast.success('Transferencia directa enviada exitosamente')
      }

      reset()
      refreshPending()
    } catch (error) {
      toast.error(getApiMessage(error, 'Error al procesar la operacion'))
    }
  }

  const handleCancelMyRequest = (transfer: TransferListItem) => {
    if (!canRequestTransfer) {
      toast.error('No tienes permiso para cancelar solicitudes')
      return
    }

    setConfirmModal({
      open: true,
      preset: 'cancel',
      itemName: 'solicitud',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }))
        try {
          await transferService.cancelTransferRequest(Number(transfer.id), {
            reason: 'Solicitud cancelada desde POS.',
          })
          toast.success('Solicitud cancelada')
          refreshPending()
        } catch (error) {
          toast.error(getApiMessage(error, 'No se pudo cancelar la solicitud'))
        }
      }
    })
  }

  const handleRejectRequest = (transfer: TransferListItem) => {
    if (!canSendTransfer) {
      toast.error('No tienes permiso para rechazar solicitudes')
      return
    }

    setConfirmModal({
      open: true,
      preset: 'reject',
      itemName: 'solicitud',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }))
        try {
          await transferService.rejectTransferRequest(Number(transfer.id), {
            reason: 'Solicitud rechazada desde POS.',
          })
          toast.success('Solicitud rechazada')
          refreshPending()
        } catch (error) {
          toast.error(getApiMessage(error, 'No se pudo rechazar la solicitud'))
        }
      }
    })
  }

  const handleCancelSentTransfer = (transfer: TransferListItem) => {
    if (!canSendTransfer) {
      toast.error('No tienes permiso para cancelar transferencias enviadas')
      return
    }

    setConfirmModal({
      open: true,
      preset: 'cancel',
      itemName: 'transferencia',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }))
        try {
          await transferService.cancelSentTransfer(Number(transfer.id), {
            reason: 'Transferencia cancelada desde POS.',
          })
          toast.success('Transferencia cancelada')
          refreshPending()
        } catch (error) {
          toast.error(getApiMessage(error, 'No se pudo cancelar la transferencia'))
        }
      }
    })
  }

  const handleClose = () => {
    reset()
    onOpenChange(false)
  }

  const openExportModal = (selection: TransferExportSelection = null) => {
    if (!canExportTransfer) {
      toast.error('No tienes permiso para exportar transferencias')
      return
    }

    setExportSelection(selection)
    setIsExportModalOpen(true)
  }

  const handleExport = async (format: ExportFormat) => {
    if (!canExportTransfer) {
      toast.error('No tienes permiso para exportar transferencias')
      return
    }

    if (!activeBranchId) {
      toast.error('Selecciona una sucursal para exportar transferencias')
      return
    }

    if (format === 'word') {
      toast.error('Formato no disponible para transferencias')
      return
    }

    const transferRequestIds = exportSelection?.transferRequestIds ?? (
      transferAction === 'send'
        ? pendingSends.map(item => Number(item.id))
        : transferAction === 'request'
          ? myPendingRequests.map(item => Number(item.id))
          : []
    )

    const inventoryTransferIds = exportSelection?.inventoryTransferIds ?? (
      transferAction === 'send'
        ? sentPendingTransfers.map(item => Number(item.id))
        : transferAction === 'receive'
          ? pendingReceives.map(item => Number(item.id))
          : []
    )

    try {
      const response = await transferService.exportTransferDetails({
        context: transferAction,
        format: (format === 'excel' ? 'xlsx' : format) as TransferExportFormat,
        sucursal_id: activeBranchId,
        transfer_request_ids: transferRequestIds,
        inventory_transfer_ids: inventoryTransferIds,
      })

      if (format === 'pdf') {
        const url = URL.createObjectURL(response.data)
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = url
        document.body.appendChild(iframe)
        iframe.onload = () => {
          setTimeout(() => {
            iframe.contentWindow?.print()
          }, 100)
        }
        toast.success('Preparando documento para imprimir')
      } else {
        const contentDisposition = response.headers['content-disposition'] as string | undefined
        const filename = filenameFromDisposition(
          contentDisposition,
          `transferencias-${transferAction}.${format === 'excel' ? 'xlsx' : format}`,
        )

        downloadBlob(response.data, filename)
        toast.success('Exportacion generada correctamente')
      }
    } catch (error) {
      toast.error(getApiMessage(error, 'No se pudo exportar transferencias'))
    }
  }

  const renderTransferSummary = (transfer: TransferListItem) => (
    <div className="space-y-1 text-xs text-slate-600">
      <p>
        De: {transfer.fromBranch} - A: {transfer.toBranch}
      </p>
      {transfer.products.slice(0, 3).map((product) => (
        <p key={`${transfer.id}-${product.name}`} className="text-slate-500">
          {product.name}: {product.quantity}
        </p>
      ))}
      {transfer.products.length > 3 && (
        <p className="text-slate-500">+{transfer.products.length - 3} productos mas</p>
      )}
    </div>
  )

  const renderEmpty = (message: string) => (
    <p className="py-4 text-center text-sm text-slate-500">{message}</p>
  )

  return (
    <>
      <Dialog
        open={open && !showInternalSendReview && !showInternalReceiveConfirm}
        onOpenChange={onOpenChange}
      >
        <DialogContent className="flex h-[85vh] flex-col overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Gestion de Transferencias</DialogTitle>
            <DialogDescription>
              Solicitar, enviar y recibir productos entre sucursales
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Tipo de operacion:</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {canSendTransfer && (
                  <button
                    type="button"
                    onClick={() => handleActionChange('send')}
                    className={`relative rounded-lg border-2 p-3 transition-all ${transferAction === 'send'
                      ? 'border-primary bg-primary/10'
                      : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    {pendingSends.length > 0 && (
                      <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white">
                        {pendingSends.length}
                      </span>
                    )}
                    <ArrowRightLeft className={`mx-auto mb-2 h-5 w-5 ${transferAction === 'send' ? 'text-primary' : 'text-slate-400'}`} />
                    <p className="text-xs font-medium">Enviar</p>
                    <p className="text-xs text-slate-600">A otra sucursal</p>
                  </button>
                )}

                {canRequestTransfer && (
                  <button
                    type="button"
                    onClick={() => handleActionChange('request')}
                    className={`rounded-lg border-2 p-3 transition-all ${transferAction === 'request'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    <Download className={`mx-auto mb-2 h-5 w-5 ${transferAction === 'request' ? 'text-green-600' : 'text-slate-400'}`} />
                    <p className="text-xs font-medium">Solicitar</p>
                    <p className="text-xs text-slate-600">De otra sucursal</p>
                  </button>
                )}

                {canReceiveTransfer && (
                  <button
                    type="button"
                    onClick={() => handleActionChange('receive')}
                    className={`relative rounded-lg border-2 p-3 transition-all ${transferAction === 'receive'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    {pendingReceives.length > 0 && (
                      <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-purple-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white">
                        {pendingReceives.length}
                      </span>
                    )}
                    <Upload className={`mx-auto mb-2 h-5 w-5 ${transferAction === 'receive' ? 'text-purple-600' : 'text-slate-400'}`} />
                    <p className="text-xs font-medium">Recibir</p>
                    <p className="text-xs text-slate-600">De otra sucursal</p>
                  </button>
                )}
              </div>
            </div>

            {availableActions.length === 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                {renderEmpty('No tienes permisos para solicitar, enviar o recibir transferencias.')}
              </div>
            )}

            {transferAction === 'request' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-green-600" />
                    <h4 className="text-sm font-medium text-slate-900">
                      Mis solicitudes pendientes
                    </h4>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-2 border-slate-200 text-slate-700 shadow-sm"
                    hidden={!canExportTransfer}
                    onClick={() => openExportModal()}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Exportar Solicitudes
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-green-200 bg-green-50 p-3">
                  {isLoadingPending ? (
                    renderEmpty('Cargando solicitudes...')
                  ) : myPendingRequests.length > 0 ? (
                    myPendingRequests.map((transfer) => (
                      <div key={transfer.id} className="mb-2 rounded-lg bg-white p-3 last:mb-0">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900">REQ-{transfer.id.padStart(4, '0')}</p>
                            <p className="text-xs text-slate-600">{transfer.date}</p>
                          </div>
                          <Badge className="bg-green-100 text-green-700">Pendiente</Badge>
                        </div>
                        {renderTransferSummary(transfer)}
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            hidden={!canExportTransfer}
                            onClick={() => openExportModal({
                              transferRequestIds: [Number(transfer.id)],
                              inventoryTransferIds: [],
                            })}
                          >
                            <Download className="mr-1 h-3 w-3" />
                            Exportar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                            onClick={() => handleCancelMyRequest(transfer)}
                          >
                            <XCircle className="mr-1 h-3 w-3" />
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmpty('No tienes solicitudes pendientes.')
                  )}
                </div>
              </div>
            )}

            {transferAction === 'send' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-600" />
                    <h4 className="text-sm font-medium text-slate-900">
                      Solicitudes que me hicieron
                    </h4>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-2 border-slate-200 text-slate-700 shadow-sm"
                    hidden={!canExportTransfer}
                    onClick={() => openExportModal()}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Exportar Envios
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-orange-200 bg-orange-50 p-3">
                  {isLoadingPending ? (
                    renderEmpty('Cargando solicitudes...')
                  ) : pendingSends.length > 0 ? (
                    pendingSends.map((transfer) => (
                      <div key={transfer.id} className="mb-2 rounded-lg bg-white p-3 last:mb-0">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900">TRF-{transfer.id.padStart(4, '0')}</p>
                            <p className="text-xs text-slate-600">{transfer.date}</p>
                          </div>
                          <Badge className="bg-orange-100 text-orange-700">Pendiente</Badge>
                        </div>
                        {renderTransferSummary(transfer)}
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            hidden={!canExportTransfer}
                            onClick={() => openExportModal({
                              transferRequestIds: [Number(transfer.id)],
                              inventoryTransferIds: [],
                            })}
                          >
                            <Download className="mr-1 h-3 w-3" />
                            Exportar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setInternalTransferToReview(transfer)
                              setShowInternalSendReview(true)
                            }}
                          >
                            <Eye className="mr-1 h-3 w-3" />
                            Revisar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                            onClick={() => handleRejectRequest(transfer)}
                          >
                            <XCircle className="mr-1 h-3 w-3" />
                            Rechazar
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmpty('No tienes solicitudes por enviar.')
                  )}
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-medium text-slate-900">
                    Transferencias enviadas pendientes de recepcion
                  </h4>
                </div>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-primary/20 bg-primary/10 p-3">
                  {isLoadingPending ? (
                    renderEmpty('Cargando transferencias...')
                  ) : sentPendingTransfers.length > 0 ? (
                    sentPendingTransfers.map((transfer) => (
                      <div key={transfer.id} className="mb-2 rounded-lg bg-white p-3 last:mb-0">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900">TRF-{transfer.id.padStart(4, '0')}</p>
                            <p className="text-xs text-slate-600">{transfer.date}</p>
                          </div>
                          <Badge className="bg-primary/15 text-primary">Por recibir</Badge>
                        </div>
                        {renderTransferSummary(transfer)}
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            hidden={!canExportTransfer}
                            onClick={() => openExportModal({
                              transferRequestIds: [],
                              inventoryTransferIds: [Number(transfer.id)],
                            })}
                          >
                            <Download className="mr-1 h-3 w-3" />
                            Exportar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                            onClick={() => handleCancelSentTransfer(transfer)}
                          >
                            <XCircle className="mr-1 h-3 w-3" />
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmpty('No tienes transferencias pendientes de recepcion.')
                  )}
                </div>
              </div>
            )}

            {transferAction === 'receive' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-purple-600" />
                    <h4 className="text-sm font-medium text-slate-900">
                      Pendientes por recibir
                    </h4>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-2 border-slate-200 text-slate-700 shadow-sm"
                    hidden={!canExportTransfer}
                    onClick={() => openExportModal()}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Exportar Recepciones
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-purple-200 bg-purple-50 p-3">
                  {isLoadingPending ? (
                    renderEmpty('Cargando recepciones...')
                  ) : pendingReceives.length > 0 ? (
                    pendingReceives.map((transfer) => (
                      <div key={transfer.id} className="mb-2 rounded-lg bg-white p-3 last:mb-0">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900">RCP-{transfer.id.padStart(4, '0')}</p>
                            <p className="text-xs text-slate-600">{transfer.date}</p>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">Por recibir</Badge>
                        </div>
                        {renderTransferSummary(transfer)}
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            hidden={!canExportTransfer}
                            onClick={() => openExportModal({
                              transferRequestIds: [],
                              inventoryTransferIds: [Number(transfer.id)],
                            })}
                          >
                            <Download className="mr-1 h-3 w-3" />
                            Exportar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setInternalTransferToReceive(transfer)
                              setShowInternalReceiveConfirm(true)
                            }}
                          >
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Confirmar
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmpty('No tienes recepciones pendientes.')
                  )}
                </div>
              </div>
            )}

            {(transferAction === 'send' || transferAction === 'request') && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      {transferAction === 'send' ? 'Desde (Mi Sucursal)' : 'Solicitar De'}
                    </label>
                    {transferAction === 'send' ? (
                      <Input disabled value={activeBranchName} className="bg-slate-50 font-medium text-slate-600" />
                    ) : (
                      <CatalogSelect
                        catalog="sucursales"
                        placeholder="Selecciona origen..."
                        value={watchFromBranch}
                        onChange={(val) => {
                          setValue('fromBranch', val, { shouldValidate: true })
                          trigger()
                        }}
                      />
                    )}
                    {errors.fromBranch && <FormError message={errors.fromBranch.message} />}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      {transferAction === 'send' ? 'Hacia' : 'Para (Mi Sucursal)'}
                    </label>
                    {transferAction === 'request' ? (
                      <Input disabled value={activeBranchName} className="bg-slate-50 font-medium text-slate-600" />
                    ) : (
                      <CatalogSelect
                        catalog="sucursales"
                        placeholder="Selecciona destino..."
                        value={watchToBranch}
                        onChange={(val) => {
                          setValue('toBranch', val, { shouldValidate: true })
                          trigger()
                        }}
                      />
                    )}
                    {errors.toBranch && <FormError message={errors.toBranch.message} />}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Productos:
                  </label>
                  <CatalogSelect
                    catalog="products"
                    placeholder="Buscar y agregar producto..."
                    returnObject={true}
                    onChange={(item) => addProductFromSearch(item as CatalogProductOption | null)}
                  />
                  {errors.products && <FormError message={errors.products.message} />}

                  <div className="max-h-60 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
                    {productFields.length > 0 ? (
                      productFields.map((product, index) => (
                        <div key={product.id} className="flex items-center gap-2 rounded-lg bg-slate-50 p-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-900">{product.productName}</p>
                            <p className="text-xs text-slate-600">
                              {product.barcode ? `CB: ${product.barcode}` : `ID: ${product.productId}`}
                              {product.stock !== undefined && ` | Stock origen: ${product.stock}`}
                            </p>
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              {...register(`products.${index}.quantity` as const, {
                                onChange: () => trigger(`products.${index}`)
                              })}
                              placeholder="Cant."
                              min="1"
                              className={errors.products?.[index]?.quantity ? 'border-red-500' : ''}
                            />
                            {errors.products?.[index]?.quantity && (
                              <p className="mt-1 text-xs text-red-500">
                                {errors.products[index]?.quantity?.message}
                              </p>
                            )}
                          </div>
                          <Button
                            type="button"
                            onClick={() => remove(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="py-4 text-center text-sm text-slate-600">
                        <Package className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                        Selecciona un producto en el buscador de arriba.
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Notas</label>
                  <Textarea
                    {...register('notes')}
                    placeholder="Notas adicionales sobre la transferencia..."
                    rows={3}
                  />
                </div>

                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="mb-2 text-sm font-medium text-slate-900">Resumen:</p>
                  <div className="space-y-1 text-sm text-slate-700">
                    <p>
                      <strong>Operacion:</strong>{' '}
                      {transferAction === 'send' ? 'Enviar productos' : 'Solicitar productos'}
                    </p>
                    <p>
                      <strong>Productos seleccionados:</strong> {productFields.length}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={isSubmitting || productFields.length === 0} className="flex-1">
                    {isSubmitting ? (
                      <DiscreteLoader />
                    ) : transferAction === 'send' ? (
                      'Enviar Productos'
                    ) : (
                      'Crear Solicitud'
                    )}
                  </Button>

                  <Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={isSubmitting}>
                    Cancelar
                  </Button>
                </div>
              </form>
            )}

            {transferAction === 'receive' && (
              <div className="flex justify-end">
                <Button variant="outline" onClick={handleClose}>
                  Cerrar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
        <ConfirmationModal
          isOpen={confirmModal.open}
          preset={confirmModal.preset}
          itemName={confirmModal.itemName}
          onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
          onConfirm={confirmModal.onConfirm}
        />
      </Dialog>

      {/* Sub-modals internos para navegación */}
      <SendReviewModal
        open={showInternalSendReview}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setShowInternalSendReview(false)
            setInternalTransferToReview(null)
          }
        }}
        selectedTransfer={internalTransferToReview}
        onConfirm={() => {
          setShowInternalSendReview(false)
          setInternalTransferToReview(null)
          setRefreshCount(c => c + 1)
        }}
      />

      <ReceiveConfirmModal
        open={showInternalReceiveConfirm}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setShowInternalReceiveConfirm(false)
            setInternalTransferToReceive(null)
          }
        }}
        selectedTransfer={internalTransferToReceive}
        onConfirm={() => {
          setShowInternalReceiveConfirm(false)
          setInternalTransferToReceive(null)
          setRefreshCount(c => c + 1)
        }}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => {
          setIsExportModalOpen(false)
          setExportSelection(null)
        }}
        onExport={handleExport}
        title="Exportar Transferencias"
        allowedFormats={['pdf']}
      />
    </>
  )
}
